import { StepEnum } from '@/api-sdk';
import { AnnouncementParams } from '@/types';
import { t } from 'i18next';

/**
 * Announces quantity changes for accessibility
 * @param element The element to dispatch the event from
 * @param type The step type
 * @param params The parameters for the announcement
 */
export const announceQuantityChange = (
  element: HTMLElement,
  type: StepEnum,
  params: AnnouncementParams,
) => {
  const announcement = getAnnouncementBasedOnQuantityStep(type, params);

  if (announcement) {
    element.dispatchEvent(
      new CustomEvent('handle-announcement', {
        composed: true,
        detail: {
          announcement,
        },
      }),
    );
  }
};

/**
 * Gets the focus announcement text for a quantity input
 * @param type The step type
 * @param params The parameters for the announcement
 */
export const getQuantityA11yName = (
  type: StepEnum,
  params: AnnouncementParams,
): string => {
  const {
    quantity,
    version,
    color,
    size,
    unitPrice,
    totalPrice,
    fastDelivery,
    discountPercentage,
  } = params;

  switch (type) {
    case StepEnum.quantity:
    case StepEnum.quantity_collapsible:
      return t('accessibility.quantityFocus', {
        quantity,
        unitPrice,
        totalPrice,
        fastDelivery: fastDelivery ? t('accessibility.fastDelivery') : '',
        discount: discountPercentage
          ? t('accessibility.discount', {
              percentage: Math.abs(discountPercentage),
            })
          : '',
      });
    case StepEnum.quantity_per_color:
    case StepEnum.quantity_per_color_collapsible:
      return t('accessibility.quantityPerColorFocus', {
        version,
        unitPrice,
      });
    case StepEnum.quantity_per_size:
    case StepEnum.quantity_per_size_collapsible:
      return t('accessibility.quantityPerSizeFocus', {
        size,
        unitPrice,
      });
    case StepEnum.quantity_per_size_per_color:
    case StepEnum.quantity_per_size_per_color_collapsible:
      return t('accessibility.quantityPerSizePerColorFocus', {
        color,
        size,
        unitPrice,
      });
    default:
      return '';
  }
};

/**
 * Gets the announcement text for a quantity change based on the step type
 * @param type The step type
 * @param params The parameters for the announcement
 */
const getAnnouncementBasedOnQuantityStep = (
  type: StepEnum,
  params: AnnouncementParams,
): string => {
  const {
    quantity,
    version,
    color,
    size,
    unitPrice,
    totalPrice,
    fastDelivery,
    discountPercentage,
  } = params;

  switch (type) {
    case StepEnum.quantity:
    case StepEnum.quantity_collapsible:
      return t('accessibility.quantitySelected', {
        quantity,
        fastDelivery: fastDelivery ? t('accessibility.fastDelivery') : '',
        discount: discountPercentage
          ? t('accessibility.discount', {
              percentage: Math.abs(discountPercentage),
            })
          : '',
        unitPrice,
        totalPrice,
      });
    case StepEnum.quantity_per_color:
    case StepEnum.quantity_per_color_collapsible:
      return t('accessibility.quantityPerColorSelected', {
        quantity,
        version,
        unitPrice,
        totalPrice,
      });
    case StepEnum.quantity_per_size:
    case StepEnum.quantity_per_size_collapsible:
      return t('accessibility.quantityPerSizeSelected', {
        quantity,
        size,
        unitPrice,
        totalPrice,
      });
    case StepEnum.quantity_per_size_per_color:
    case StepEnum.quantity_per_size_per_color_collapsible:
      return t('accessibility.quantityPerSizePerColorSelected', {
        quantity,
        color,
        size,
        unitPrice,
        totalPrice,
      });
    default:
      return '';
  }
};
